import { Component, input, output } from '@angular/core';

export interface SelectItem {
  value: string;
  label: string;
  /** Texto secundário (ex.: chave técnica); usado por combobox custom — `<select>` nativo ignora. */
  meta?: string;
  /** Cor opcional para render custom (combobox/pills). */
  colorHex?: string | null;
  /** Id do utilizador para avatar no combobox (pessoa / responsável). */
  userId?: string | null;
}

@Component({
  selector: 'app-select',
  standalone: true,
  template: `
    <label class="select-shell" [class.select-shell--inline]="inlineLabel()">
      @if (label()) {
        <span>{{ label() }}</span>
      }
      <select
        [required]="required()"
        [multiple]="multiple()"
        [disabled]="disabled()"
        [value]="singleValue()"
        (change)="onChange($event)"
      >
        @if (!multiple()) {
          <option [value]="''">{{ placeholder() }}</option>
        }
        @for (option of options(); track option.value) {
          <option [value]="option.value" [selected]="isSelected(option.value)">
            {{ option.label }}
          </option>
        }
      </select>
    </label>
  `,
  styles: [
    `
      .select-shell {
        display: grid;
        gap: 4px;
      }

      .select-shell span {
        font-size: 12px;
        color: var(--faint);
      }

      .select-shell--inline {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .select-shell--inline span {
        white-space: nowrap;
      }

      .select-shell select {
        min-height: 36px;
        width: 100%;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        color: var(--text);
        padding: 0 32px 0 10px;
        font-size: 13px;
        cursor: pointer;
        appearance: auto;
        -webkit-appearance: menulist;
      }

      .select-shell select[multiple] {
        min-height: 92px;
        padding: 8px 10px;
      }
    `,
  ],
})
export class SelectComponent {
  readonly label = input<string>('');
  readonly inlineLabel = input(false);
  readonly options = input<SelectItem[]>([]);
  readonly value = input<string | string[] | null>(null);
  readonly valueChange = output<string | string[] | null>();
  readonly placeholder = input<string>('Selecionar');
  readonly required = input<boolean>(false);
  readonly multiple = input<boolean>(false);
  readonly disabled = input<boolean>(false);

  singleValue(): string {
    const value = this.value();
    return Array.isArray(value) ? '' : (value ?? '');
  }

  isSelected(optionValue: string): boolean {
    const value = this.value();
    return Array.isArray(value) ? value.includes(optionValue) : value === optionValue;
  }

  onChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    if (this.multiple()) {
      const values = Array.from(target.selectedOptions).map((option) => option.value);
      this.valueChange.emit(values);
      return;
    }
    this.valueChange.emit(target.value || null);
  }
}
